import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import type { Channel } from "@capacitor/push-notifications";
import { supabase } from "@/integrations/supabase/client";
import { getRuntimeAppVersion } from "@/lib/appVersion";
import { safeAppUrl } from "@/lib/notificationRouting";

type PushState = "unsupported" | "default" | "granted" | "denied";
type PushPlatform = "ios" | "android" | "web";

interface PushRegistrationPayload {
  platform: PushPlatform;
  nativeToken?: string;
  endpoint?: string;
  p256dh?: string | null;
  authKey?: string | null;
  installationId: string;
  locale: string;
  timezone: string;
  appVersion: string | null;
  userAgent: string;
}

const PUSH_INSTALLATION_KEY = "voltyshare:push_installation_id";
const DEFAULT_TIMEZONE = "Europe/Kyiv";
const DEFAULT_PUSH_CHANNEL_ID = "voltyshare-default";
const ALERT_PUSH_CHANNEL_ID = "voltyshare-alerts";

/**
 * Cross-platform push subscription hook.
 * - On native (Capacitor): uses @capacitor/push-notifications (FCM/APNs).
 * - On web: uses standard Web Push via Service Worker.
 * Stores subscriptions in `push_subscriptions` table.
 *
 * NOTE: Sending pushes from server requires VAPID keys (web) and
 * configured FCM/APNs server credentials (native).
 */
export const usePushNotifications = (vapidPublicKey?: string) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<PushState>("default");
  const [busy, setBusy] = useState(false);
  const autoRefreshRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window) && !Capacitor.isNativePlatform()) {
      setState("unsupported");
      return;
    }
    if (Capacitor.isNativePlatform()) {
      import("@capacitor/push-notifications")
        .then(async ({ PushNotifications }) => {
          await createAndroidPushChannels(PushNotifications, {
            defaultName: t("runtime.push.defaultChannelName"),
            alertsName: t("runtime.push.alertsChannelName"),
            defaultDescription: t("runtime.push.defaultChannelDescription"),
            alertsDescription: t("runtime.push.alertsChannelDescription"),
          });
          return PushNotifications.checkPermissions();
        })
        .then((perm) => setState(perm.receive === "granted" ? "granted" : perm.receive === "denied" ? "denied" : "default"))
        .catch(() => setState("unsupported"));
      return;
    }
    if ("Notification" in window) setState(Notification.permission as PushState);
  }, [t]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let actionHandle: { remove: () => Promise<void> } | null = null;

    import("@capacitor/push-notifications")
      .then(({ PushNotifications }) =>
        PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
          const data = event.notification?.data ?? {};
          const rawUrl = typeof data.url === "string"
            ? data.url
            : typeof data.action_url === "string"
              ? data.action_url
              : typeof data.deep_link === "string"
                ? data.deep_link
                : typeof data.actionUrl === "string"
                  ? data.actionUrl
                  : null;
          if (rawUrl) openAppUrl(rawUrl);
        }),
      )
      .then((handle) => {
        if (cancelled) {
          void handle.remove().catch(() => undefined);
        } else {
          actionHandle = handle;
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      void actionHandle?.remove().catch(() => undefined);
    };
  }, []);

  const subscribe = useCallback(async () => {
    setBusy(true);
    try {
      const locale = i18n.resolvedLanguage || i18n.language || navigator.language || "en";
      // ------- Native path (Capacitor) -------
      if (Capacitor.isNativePlatform()) {
        const mod = await import("@capacitor/push-notifications").catch(() => null);
        if (mod?.PushNotifications) {
          let perm = await mod.PushNotifications.checkPermissions();
          if (perm.receive !== "granted" && perm.receive !== "denied") {
            perm = await mod.PushNotifications.requestPermissions();
          }
          if (perm.receive !== "granted") {
            setState("denied");
            return false;
          }

          if (Capacitor.getPlatform() === "android") {
            await createAndroidPushChannels(mod.PushNotifications, {
              defaultName: t("runtime.push.defaultChannelName"),
              alertsName: t("runtime.push.alertsChannelName"),
              defaultDescription: t("runtime.push.defaultChannelDescription"),
              alertsDescription: t("runtime.push.alertsChannelDescription"),
            });
          }

          const registered = await new Promise<boolean>((resolve) => {
            let done = false;
            const handles: Array<{ remove: () => Promise<void> }> = [];
            const finish = async (ok: boolean) => {
              if (done) return;
              done = true;
              window.clearTimeout(timer);
              await Promise.all(handles.map((handle) => handle.remove().catch(() => undefined)));
              resolve(ok);
            };
            const timer = window.setTimeout(() => void finish(false), 7000);

            const registrationListener = mod.PushNotifications.addListener("registration", async (token: { value: string }) => {
              try {
                const platform: PushPlatform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
                const { data: u } = await supabase.auth.getUser();
                if (!u.user) {
                  await finish(false);
                  return;
                }

                await registerPushSubscription({
                  platform,
                  nativeToken: token.value,
                  installationId: getInstallationId(platform),
                  locale,
                  timezone: getTimeZone(),
                  appVersion: getRuntimeAppVersion(),
                  userAgent: navigator.userAgent.slice(0, 500),
                });
                await finish(true);
              } catch (error) {
                console.warn("Native push registration failed:", error);
                await finish(false);
              }
            }).then((handle: { remove: () => Promise<void> }) => {
              handles.push(handle);
              return handle;
            });

            const registrationErrorListener = mod.PushNotifications.addListener("registrationError", () => {
              void finish(false);
            }).then((handle: { remove: () => Promise<void> }) => {
              handles.push(handle);
              return handle;
            });

            void Promise.all([registrationListener, registrationErrorListener])
              .then(() => mod.PushNotifications.register())
              .catch(() => void finish(false));
          });
          if (!registered) return false;
          autoRefreshRef.current = true;
          setState("granted");
          return true;
        }
      }

      // ------- Web Push path -------
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState("unsupported");
        return false;
      }
      const perm = await Notification.requestPermission();
      setState(perm as PushState);
      if (perm !== "granted") return false;

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub && vapidPublicKey) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
      }
      if (!sub) return false;

      const json = sub.toJSON();
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return false;

      await registerPushSubscription({
        platform: "web",
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? null,
        authKey: json.keys?.auth ?? null,
        installationId: getInstallationId("web"),
        locale,
        timezone: getTimeZone(),
        appVersion: getRuntimeAppVersion(),
        userAgent: navigator.userAgent.slice(0, 500),
      });
      return true;
    } finally {
      setBusy(false);
    }
  }, [i18n.language, i18n.resolvedLanguage, t, vapidPublicKey]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || state !== "granted" || autoRefreshRef.current) return;
    autoRefreshRef.current = true;
    void subscribe().then((ok) => {
      if (!ok) autoRefreshRef.current = false;
    });
  }, [state, subscribe]);

  return { state, subscribe, busy };
};

async function registerPushSubscription(payload: PushRegistrationPayload) {
  const { error } = await supabase.functions.invoke("register-push-subscription", {
    body: payload,
  });
  if (error) throw error;
}

async function createAndroidPushChannels(PushNotifications: {
  createChannel?: (channel: Channel) => Promise<void>;
}, copy: {
  defaultName: string;
  alertsName: string;
  defaultDescription: string;
  alertsDescription: string;
}) {
  if (Capacitor.getPlatform() !== "android") return;
  await Promise.all([
    PushNotifications.createChannel?.({
      id: DEFAULT_PUSH_CHANNEL_ID,
      name: copy.defaultName,
      description: copy.defaultDescription,
      importance: 4,
      visibility: 1,
      sound: "default",
    }).catch(() => undefined),
    PushNotifications.createChannel?.({
      id: ALERT_PUSH_CHANNEL_ID,
      name: copy.alertsName,
      description: copy.alertsDescription,
      importance: 5,
      visibility: 1,
      sound: "default",
      vibration: true,
      lights: true,
    }).catch(() => undefined),
  ]);
}

function openAppUrl(rawUrl: string) {
  const url = safeAppUrl(rawUrl);
  if (url.startsWith("/")) {
    window.history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  window.location.href = url;
}

function getInstallationId(platform: string) {
  if (typeof window === "undefined") return `${platform}:server`;
  const key = `${PUSH_INSTALLATION_KEY}:${platform}`;
  const next = `${platform}:${crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    window.localStorage.setItem(key, next);
  } catch {
    return next;
  }
  return next;
}

function getTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}
